
import { useState, useEffect, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/services/utils/loggingUtils';
import { toast } from 'sonner';

const logger = createLogger('useSessionInitialization');

export const useSessionInitialization = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isInitializing = useRef(true);

  // Initialize auth state and listen for changes
  useEffect(() => {
    if (!isInitializing.current) {
      return;
    }
    
    logger.info('Initializing authentication state');
    setIsLoading(true);
    isInitializing.current = false;
    
    const initializeAuth = async () => {
      try {
        // Get the initial session
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error('Error getting initial session:', error);
          throw error;
        }
        
        logger.info('Initial session loaded:', data.session ? 'Active' : 'None');
        
        setSession(data.session);
        setUser(data.session?.user ?? null);
      } catch (error) {
        logger.error('Failed to initialize auth state:', error);
        toast.error('Failed to authenticate. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeAuth();
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        logger.info('Auth event:', event);
        
        switch (event) {
          case 'SIGNED_IN':
            setSession(newSession);
            setUser(newSession?.user ?? null);
            break;
            
          case 'SIGNED_OUT':
            setSession(null);
            setUser(null);
            break;
            
          case 'TOKEN_REFRESHED':
            logger.info('Token refreshed');
            setSession(newSession);
            break;
            
          case 'USER_UPDATED':
            setSession(newSession);
            setUser(newSession?.user ?? null);
            break;
        }
        
        setIsLoading(false);
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    user,
    setUser,
    session,
    setSession,
    isLoading,
    setIsLoading
  };
};
